import React, { useCallback, useEffect, useState } from "react";
import { UsersThree, ArrowClockwise, CaretDown, CaretUp, CheckCircle, MinusCircle } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";

function DayBlock({ d }) {
  const [open, setOpen] = useState(false);
  const postedTotal = d.posts.reduce((n, p) => n + p.posted.length, 0);
  return (
    <div className="border border-tac-border rounded-sm">
      <button
        type="button"
        data-testid={`blast-day-${d.day}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-tac-surface2/50 transition-colors"
      >
        <span className="text-[12px] font-bold font-mono-tac text-tac-text">{d.day}</span>
        <span className="flex items-center gap-2 text-[11px] text-tac-dim">
          {d.posts.length} post{d.posts.length === 1 ? "" : "s"} · {postedTotal} group post{postedTotal === 1 ? "" : "s"}
          {open ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {d.posts.map((p) => (
            <div key={p.caption_head}>
              <div className="text-[11px] font-semibold text-tac-text mb-1 line-clamp-1">{p.caption_head}</div>
              <div className="flex flex-wrap gap-1">
                {p.posted.map((g) => (
                  <span key={g} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-sm bg-tac-primary/10 text-tac-primary border border-tac-primary/30">
                    <CheckCircle size={10} weight="fill" /> {g}
                  </span>
                ))}
                {p.skipped.map((g) => (
                  <span key={g} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-sm bg-tac-surface2 text-tac-faint border border-tac-border">
                    <MinusCircle size={10} /> {g}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const BlastHistoryCard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showCoverage, setShowCoverage] = useState(false);

  const load = useCallback(() => {
    setErr(null);
    api.get("/metrics/blast-history")
      .then((r) => setData(r.data))
      .catch(() => setErr("Couldn't load blast history"));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (user?.role !== "admin") return null;

  const maxPosted = data?.coverage?.length ? Math.max(...data.coverage.map((c) => c.posted)) : 0;

  return (
    <div className="bg-tac-surface border border-tac-border rounded-sm p-4" data-testid="blast-history-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-tac-dim">
          <UsersThree size={14} weight="fill" style={{ color: "#1877F2" }} /> Group Blast History
        </div>
        <button
          type="button"
          data-testid="blast-history-refresh"
          onClick={load}
          className="p-1 text-tac-faint hover:text-tac-text transition-colors"
          title="Refresh"
        >
          <ArrowClockwise size={13} weight="bold" />
        </button>
      </div>

      {err && <div className="text-[11px] text-tac-danger">{err}</div>}
      {!err && !data && <div className="text-[11px] text-tac-faint">Loading…</div>}
      {data && !data.days.length && (
        <div className="text-[11px] text-tac-faint">
          No blasts logged yet — history starts recording the next time you use "Post to All Groups".
        </div>
      )}

      {data && data.days.length > 0 && (
        <div className="space-y-2">
          {data.days.map((d) => <DayBlock key={d.day} d={d} />)}

          <button
            type="button"
            data-testid="blast-coverage-toggle"
            onClick={() => setShowCoverage((v) => !v)}
            className="text-[10px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
          >
            {showCoverage ? "Hide" : "Show"} per-group coverage
          </button>
          {showCoverage && (
            <div className="space-y-1.5">
              {data.coverage.map((c) => (
                <div key={c.group} data-testid={`blast-coverage-${c.group}`}>
                  <div className="flex items-center justify-between text-[11px] text-tac-text">
                    <span className="truncate">{c.group}</span>
                    <span className="font-mono-tac text-tac-dim shrink-0">
                      {c.posted} posted{c.skipped ? ` · ${c.skipped} skipped` : ""}
                    </span>
                  </div>
                  <div className="h-1.5 bg-tac-surface2 rounded-sm overflow-hidden">
                    <div className="h-full bg-tac-primary/80" style={{ width: `${maxPosted ? Math.round((c.posted / maxPosted) * 100) : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[9px] text-tac-faint">History kept 30 days. Logged whenever "Open & mark posted" or "Skip" is clicked in the blast tool.</div>
        </div>
      )}
    </div>
  );
};

export default BlastHistoryCard;
